import React, { useState, useEffect } from 'react';
import { Award, Briefcase, ChevronRight, Globe, Layers, Target } from 'lucide-react';
import { API_URL } from '../api';
import { useTranslation } from 'react-i18next';

const AboutPage = ({ onBack }) => {
  const { t } = useTranslation();
  const [aboutContent, setAboutContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAbout = async () => {
      try {
        const response = await fetch(`${API_URL}/about`);
        const data = await response.json();
        setAboutContent(data);
      } catch (err) {
        console.error('Failed to load about content:', err);
      }
      setLoading(false);
    };
    fetchAbout();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-12 h-12 border-4 border-levelup-accent border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const iconMap = {
    award: Award,
    briefcase: Briefcase,
    target: Target,
    layers: Layers
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="about-page">
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center text-levelup-teal hover:text-levelup-accent mb-6 transition-colors"
          data-testid="about-back-button"
        >
          <ChevronRight className="rotate-180 mr-1" size={20} />
          {t('common.back') || 'Back'}
        </button>
      )}

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-levelup-dark text-white p-8">
          <h1 className="text-3xl font-bold mb-2" data-testid="about-title">
            {aboutContent?.title || 'About Skilling In a Box'}
          </h1>
          <p className="text-gray-300">{t('about.subtitle')}</p>
        </div>

        <div className="p-8 space-y-10">
          {/* Overview */}
          <section>
            <h2 className="text-xl font-bold text-levelup-dark mb-4 flex items-center">
              <Globe className="mr-2 text-levelup-accent" size={24} />
              {aboutContent?.content?.overview?.title}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {aboutContent?.content?.overview?.description}
            </p>
          </section>

          {/* Mission */}
          <section className="bg-levelup-light rounded-lg p-6">
            <h2 className="text-xl font-bold text-levelup-dark mb-4">
              {aboutContent?.content?.mission?.title}
            </h2>
            <p className="text-gray-700 leading-relaxed italic">
              "{aboutContent?.content?.mission?.description}"
            </p>
          </section>

          {/* Skilling Pillars */}
          <section>
            <h2 className="text-xl font-bold text-levelup-dark mb-6">Skilling Pillars</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {aboutContent?.content?.pillars?.map((pillar, index) => {
                const IconComponent = iconMap[pillar.icon] || Award;
                return (
                  <div 
                    key={index}
                    className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow"
                    data-testid={`pillar-${index}`}
                  >
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 rounded-full bg-levelup-accent/20 flex items-center justify-center mr-3">
                        <IconComponent className="text-levelup-accent" size={20} />
                      </div>
                      <h3 className="font-bold text-levelup-dark">{pillar.title}</h3>
                    </div>
                    <p className="text-gray-600 text-sm">{pillar.description}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* How It Works */}
          <section>
            <h2 className="text-xl font-bold text-levelup-dark mb-6">
              {aboutContent?.content?.how_it_works?.title}
            </h2>
            <div className="space-y-4">
              {aboutContent?.content?.how_it_works?.steps?.map((step, index) => (
                <div key={index} className="flex items-start" data-testid={`step-${step.step}`}>
                  <div className="w-8 h-8 rounded-full bg-levelup-accent text-levelup-darker flex items-center justify-center font-bold text-sm flex-shrink-0 mr-4">
                    {step.step}
                  </div>
                  <div>
                    <h4 className="font-semibold text-levelup-dark">{step.title}</h4>
                    <p className="text-gray-600 text-sm">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Solution Areas */}
          <section className="bg-levelup-dark text-white rounded-lg p-6">
            <h2 className="text-xl font-bold mb-4">
              {aboutContent?.content?.solution_areas?.title}
            </h2>
            <div className="flex flex-wrap gap-3">
              {aboutContent?.content?.solution_areas?.areas?.map((area, index) => (
                <span 
                  key={index}
                  className="px-4 py-2 bg-white/10 rounded-full text-sm"
                >
                  {area}
                </span>
              ))}
            </div>
          </section>

          {/* Eligibility */}
          <section>
            <h2 className="text-xl font-bold text-levelup-dark mb-4">
              {aboutContent?.content?.eligibility?.title}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {aboutContent?.content?.eligibility?.description}
            </p>
          </section>

          {/* Support */}
          <section className="border-t pt-8">
            <h2 className="text-xl font-bold text-levelup-dark mb-4">
              {aboutContent?.content?.support?.title}
            </h2>
            <p className="text-gray-600 mb-4">
              {aboutContent?.content?.support?.description}
            </p>
            <a
              href={aboutContent?.content?.support?.support_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-levelup-teal hover:text-levelup-accent transition-colors"
            >
            Visit Support Portal
              <ChevronRight size={18} className="ml-1" />
            </a>
          </section>
        </div>
      </div>
    </div>
  );
};

// Partner Admin Setup Wizard Component

export default AboutPage;
